import { FormControlLabel, FormGroup, Switch } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { ArrowDropdown, Checkbox, LanguageDropdown, SearchBox, Title } from '@ifs-gcp/components';
import type { ChecklistChild, TranslationText } from '@ifs-gcp/core/models';

import { getTranslation } from '@/utils/translations';

import type { DropdownItem } from '../../checklist';
import styles from './checklist-header.module.scss';

type ChecklistHeaderProps = {
  name: TranslationText;
  chapters: ChecklistChild['props']['chaptersList'];
  language: string;
  supportedLanguages: string[];
  isValidationEnabled: boolean;
  isErrorsOnlyVisibility: boolean;
  columnOptions: DropdownItem<string>[];
  searchResults: { scrollId: string }[];
  searchText: string;
  setIsValidationEnabled: (isChecked: boolean) => void;
  setIsErrorsOnlyVisibility: (isChecked: boolean) => void;
  handleSetSearch: (text: string) => void;
  handleScrollToRow: (scrollId: string) => void;
  handleLanguageChange: (language: string) => void;
};

export const ChecklistHeader = observer(
  ({
    name,
    chapters,
    language,
    supportedLanguages,
    isValidationEnabled,
    isErrorsOnlyVisibility,
    searchResults,
    searchText,
    setIsValidationEnabled,
    setIsErrorsOnlyVisibility,
    handleSetSearch,
    handleScrollToRow,
    handleLanguageChange,
  }: ChecklistHeaderProps) => {
    const { t } = useTranslation();

    const handleToggleValidation = (isChecked: boolean) => {
      setIsValidationEnabled(isChecked);

      /**
       * Disable errors only visibility if we are turning validation off
       */
      if (!isChecked && isErrorsOnlyVisibility) {
        setIsErrorsOnlyVisibility(false);
      }
    };

    const mainChapters = chapters.map<DropdownItem<string>>(
      ({ uuId, chapterNumber, headerText }) => ({
        value: uuId,
        label: `${chapterNumber}. ${getTranslation(headerText, language)}`,
      }),
    );

    return (
      <div className={styles.wrapper}>
        <div className={styles.actions}>
          <Title>{getTranslation(name, language)}</Title>
          <ArrowDropdown<string>
            items={mainChapters}
            title={t('audit.checklist.chapters')}
            onChoose={handleScrollToRow}
            renderDropdownButton={() => (
              <span className={styles.chapter}>{t('audit.checklist.chapter')}</span>
            )}
          />

          <FormGroup className={styles.formGroup}>
            <FormControlLabel
              control={
                <Switch
                  className={styles.switch}
                  checked={isValidationEnabled}
                  onChange={(_, checked) => handleToggleValidation(checked)}
                  classes={{
                    track: styles.track,
                    thumb: styles.thumb,
                    checked: styles.checked,
                  }}
                />
              }
              classes={{
                label: styles.label,
              }}
              label={t('audit.checklist.validation')}
            />

            {isValidationEnabled && (
              <Checkbox
                label={t('audit.checklist.errorsOnly')}
                checked={isErrorsOnlyVisibility}
                onChange={() => setIsErrorsOnlyVisibility(!isErrorsOnlyVisibility)}
              />
            )}
          </FormGroup>
        </div>

        <div className={styles.actions} data-test-id="checklist-search-language-container">
          <SearchBox
            results={searchResults}
            searchText={searchText}
            onSearch={handleSetSearch}
            onChangeActive={handleScrollToRow}
          />
          <LanguageDropdown
            selectedLanguage={language}
            supportedLanguages={supportedLanguages}
            onChange={handleLanguageChange}
          />
        </div>
      </div>
    );
  },
);
